"use client";
import { MobileMapBottomMargin } from "@cmp-src/data-display/Card/classes";
import { Image } from "@cmp-src/data-display/Image/Image";
import {
  ArrowLeftIcon,
  FavoriteFilledIcon,
  FavoriteIcon
} from "@cmp-src/icons/icons";
import { ResourceLabel } from "@cmp-src/labels/ResourceLabel/ResourceLabel";
import { Button } from "@cmp-src/primitives/button";
import { Switch } from "@cmp-src/primitives/Switch";
import { ScrollArea } from "@cmp-src/primitives/scroll-area";
import { IResource, ISchedule, ResourceType } from "@gettactic/api";
import { cn, UseDisclosureReturn } from "@gettactic/helpers";
import { useBreakpoint } from "@gettactic/helpers/src/hooks/useBreakpoint";
import {
  canManageResource,
  canScheduleOthers
} from "@gettactic/helpers/src/permissions";
import { dedicatedSeatCount } from "@gettactic/helpers/src/resources/dedicatedSeats";
import { getReservationExtensionDays } from "@gettactic/helpers/src/resources/defines";
import { getResourceCoverUrl } from "@gettactic/helpers/src/resources/getResourceCoverUrl";
import { isAllowedToReserve } from "@gettactic/helpers/src/resources/isAllowedToReserve";
import { useAuthenticated, useSaveResourceFavorite } from "@gettactic/hooks";
import React, { useCallback, useMemo } from "react";
import { DedicatedReleases } from "@/features/map/components/DedicatedReleases";
import { useHidesResourceIdentity } from "@/features/map/hooks/useHidesResourceIdentity";
import { useMap } from "@/features/map/MapProvider";
import { DelegatedPermissionRequestContent } from "@/features/reserve/components/Reserve/DelegatedPermissionRequest";
import { Reserve } from "@/features/reserve/components/Reserve/Reserve";
import { useReserveForm } from "@/features/reserve/components/Reserve/ReserveFormProvider";
import { ReserveOptions } from "@/features/reserve/components/Reserve/types";
import { RoomSelectionList } from "@/features/reserve/components/RoomSelection/RoomSelectionList";
import { useIsMultiRoomSelectionEnabled } from "@/features/reserve/hooks/useIsMultiRoomSelectionEnabled";
import { useRoomSelection } from "@/features/reserve/providers/roomSelectionContext";
import { TimeZoneMismatchTooltip } from "@/features/settings/components/TimeZoneMismatchAlert";
import { isResourceAvailableByCapacity } from "./availabilityUtils";
import { MapReserveCardActions } from "./MapReserveCardActions";
import { MapReserveCardMultiRoomHeader } from "./MapReserveCardMultiRoomHeader";
export interface MapReserveCardProps
  extends Omit<ReserveOptions, "resource" | "resourceType"> {
  resourceType?: ResourceType;
  resource?: IResource | null;
  onCancel?: () => void;
  onClose?: () => void;
  isOpen?: boolean;
  dayViewDisclosure?: UseDisclosureReturn;
  requireDelegatedPermission?: boolean;
  msAuthUrl?: string;
  isCompact?: boolean;
  isModal?: boolean;
  onSuccess?: () => void;
  isOnReservationModal?: boolean;
}
const classes = {
  Card: `${MobileMapBottomMargin} absolute inset-0 z-sticky md:m-4 p-0 overflow-hidden data-[state=open]:zoom-in-95 data-[state=open]:slide-in-from-bottom-2/3 data-[state=open]:fade-in-0 data-[state=open]:animate-in data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:fade-out-0 data-[state=closed]:animate-out`,
  CardContent: "p-0 overflow-hidden h-full flex flex-col",
  CoverWrapper: "relative h-60 w-full overflow-hidden",
  CoverImage:
    "flex h-full w-full items-center justify-center text-center bg-neutral-200",
  CoverControlsWrapper: "absolute inset-0 top-5 md:pt-0 p-5",
  CoverControlsContainer: "flex justify-between",
  BackButton: "rounded-full bg-black/50",
  BackIcon: "size-4 text-white",
  FavoriteButton: "bg-white",
  FavoriteIcon: "size-4 text-neutral-600",
  ScrollArea: "flex flex-1",
  ScrollAreaContent: "flex flex-col gap-6 p-3 pt-6",

  // card should fade gradient to top
  ActionButtonsWrapper:
    "p-3 flex flex-col gap-2.5 from-white/100 via-white/80 bg-gradient-to-t from-white to-transparent"
};

export const MapReserveCardInner: React.FC<
  MapReserveCardProps &
    Required<Pick<MapReserveCardProps, "resource">> & {
      haveAvailableSlots: boolean;
      /** The desk's reservations for the day, grouped per schedule. */
      resourceSchedules: ISchedule[][];
    }
> = ({
  resource,
  onCancel,
  dayViewDisclosure,
  msAuthUrl,
  requireDelegatedPermission,
  haveAvailableSlots,
  resourceSchedules
}) => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const { isBreakpoint } = useBreakpoint();

  const isMd = isBreakpoint("md");
  const { form, submitReservation, isOnReservationModal, schedule } =
    useReserveForm();
  const onSubmit = form.handleSubmit(submitReservation);

  const isMultiRoomSelectionEnabled = useIsMultiRoomSelectionEnabled();
  const { rooms, addRoom, removeRoom, clearRooms } = useRoomSelection();
  const { startReserve, floor, floors, setFloor } = useMap();
  // Whose seat each release is must stay hidden where office privacy hides
  // owners, as on the map popover.
  const hidesIdentity = useHidesResourceIdentity(
    resource?.office_id,
    resource?.type
  );

  // The rooms of a reservation can sit on different floors, and `startReserve` only
  // moves the card. Bring the map along, or the spot and the badges of the room
  // being viewed stay behind on the floor the map was already showing.
  const viewRoom = useCallback(
    (room: IResource) => {
      if (room.parent_id && room.parent_id !== floor?.id) {
        const targetFloor = floors?.find((item) => item.id === room.parent_id);
        if (targetFloor) setFloor(targetFloor);
      }
      startReserve(room);
    },
    [floor?.id, floors, setFloor, startReserve]
  );

  // Adding rooms is a create-time affordance: editing the rooms of an existing
  // reservation goes through a different update path we don't support yet.
  const canSelectMoreRooms =
    isMultiRoomSelectionEnabled &&
    !schedule &&
    resource?.type === "meeting_room";

  const isRoomSelected = rooms.some((room) => room.id === resource?.id);

  // Keep the selection anchored to the room the card is showing. Landing on a room
  // outside the current selection means the card moved to a different reservation
  // altogether, so the selection restarts from it.
  React.useEffect(() => {
    if (!canSelectMoreRooms) {
      if (rooms.length > 0) clearRooms();
      return;
    }
    if (!isRoomSelected && resource) {
      clearRooms();
      addRoom(resource);
    }
  }, [
    canSelectMoreRooms,
    isRoomSelected,
    resource,
    rooms.length,
    addRoom,
    clearRooms
  ]);

  const selectedRooms = canSelectMoreRooms ? rooms : [];

  // The picking prompt sits at the bottom of the map, right where the day view
  // panel is — it has to step aside before the map is armed.
  const handleStartPicking = useCallback(() => {
    dayViewDisclosure?.onOpenChange(false);
  }, [dayViewDisclosure]);

  const handleRemoveRoom = useCallback(
    (roomId: string) => {
      // Dropping the room being shown hands the card over to the next one.
      if (roomId === resource?.id) {
        const nextRoom = rooms.find((room) => room.id !== roomId);
        if (!nextRoom) return;
        removeRoom(roomId);
        viewRoom(nextRoom);
        return;
      }
      removeRoom(roomId);
    },
    [resource?.id, rooms, removeRoom, viewRoom]
  );
  const coverImageUrl = resource
    ? getResourceCoverUrl(resource, 300)
    : undefined;
  const { toggleResourceFavorite, isLoading: isFavoriteLoading } =
    useSaveResourceFavorite();

  // const isDemo = React.useMemo(() => isDemoDomain(), []);

  const { startTime, durationMins, currentOffice } = useReserveForm();

  const officeTimeZone = currentOffice?.time_zone ?? "";
  const office = currentOffice;
  const entity = resource?.type;
  const startDate = startTime;
  const resourceType = resource?.type;

  const allowReserveByMaxDate = useMemo(() => {
    return isAllowedToReserve(
      startDate,
      officeTimeZone,
      office,
      resourceType ?? ("workspace" as ResourceType),
      authenticatedUser.user?.organization_user?.roles,
      getReservationExtensionDays(authenticatedUser.user?.email)
    );
  }, [startDate, officeTimeZone, office, resource?.id, durationMins]);
  const allowReserveOnBehalf = canScheduleOthers(authenticatedUser);

  const meetingsDisabled =
    !authenticatedUser?.organization?.meeting_room_bookings_enabled;

  // Recalculate availability using client-side check when in reservation modal
  const actuallyHaveAvailableSlots = React.useMemo(() => {
    if (isOnReservationModal && resource && startTime && durationMins) {
      const schedules = resourceSchedules || [];
      return isResourceAvailableByCapacity(
        resource,
        schedules.flat(),
        startTime,
        durationMins
      );
    }
    return haveAvailableSlots;
  }, [
    isOnReservationModal,
    resource,
    startTime,
    durationMins,
    resourceSchedules,
    haveAvailableSlots
  ]);
  const allowReserve = React.useMemo(() => {
    if (!resource || !resource.is_available) {
      return false;
    }
    if (allowReserveOnBehalf) {
      return true;
    }
    const result =
      allowReserveByMaxDate &&
      actuallyHaveAvailableSlots &&
      (resource.type !== "meeting_room" || !meetingsDisabled);
    return result;
  }, [
    allowReserveByMaxDate,
    allowReserveOnBehalf,
    meetingsDisabled,
    resource,
    actuallyHaveAvailableSlots
  ]);

  const handleConnectOutlook = useCallback(() => {
    window.open(msAuthUrl);
  }, [msAuthUrl]);

  return (
    <>
      <div className={classes.CoverWrapper}>
        {coverImageUrl && (
          <Image
            src={coverImageUrl}
            wrapperClassName={classes.CoverImage}
            className={"h-full object-contain"}
          />
        )}
        <div className={classes.CoverControlsWrapper}>
          <div className={classes.CoverControlsContainer}>
            <Button
              variant={"circleIcon"}
              className={classes.BackButton}
              onClick={onCancel}
              size={"icon"}
            >
              <ArrowLeftIcon className={classes.BackIcon} />
            </Button>
            {!requireDelegatedPermission && (
              <Button
                disabled={isFavoriteLoading}
                variant={"circleIcon"}
                className={classes.FavoriteButton}
                size={"icon"}
                onClick={async (event) => {
                  event.stopPropagation();
                  event.preventDefault();

                  if (resource) await toggleResourceFavorite(resource);
                }}
              >
                {resource?.favorite_id ? (
                  <FavoriteFilledIcon className={classes.FavoriteIcon} />
                ) : (
                  <FavoriteIcon className={classes.FavoriteIcon} />
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
      {requireDelegatedPermission ? (
        <div className="p-3">
          <DelegatedPermissionRequestContent
            msAuthUrl={msAuthUrl ?? ""}
            handleConnectOutlook={handleConnectOutlook}
          />
        </div>
      ) : (
        <>
          <ScrollArea className={classes.ScrollArea}>
            <div className={classes.ScrollAreaContent}>
              <div className={"flex w-full items-center gap-0"}>
                {resource && selectedRooms.length > 1 ? (
                  <MapReserveCardMultiRoomHeader
                    roomsCount={selectedRooms.length}
                    viewingRoom={resource}
                  />
                ) : null}
                {resource && selectedRooms.length <= 1 && (
                  <ResourceLabel
                    className={"flex-1"}
                    infoWrapperClassName={"gap-[8px]"}
                    resource={resource}
                    showPicture={false}
                    allowFavorite={false}
                    headingClassName={"text-[18px]"}
                    hierarchyRight={
                      <TimeZoneMismatchTooltip
                        resourceOfficeId={resource.office_id}
                      />
                    }
                    headerRight={
                      dayViewDisclosure ? (
                        <Switch
                          onCheckedChange={dayViewDisclosure.onOpenChange}
                          checked={dayViewDisclosure.isOpen}
                          variant={"squared"}
                          size={"xs"}
                        />
                      ) : undefined
                    }
                  />
                )}
                <div />
              </div>
              {resource?.is_dedicated ? (
                <DedicatedReleases
                  resourceId={resource.id}
                  className="mt-0"
                  timeZone={officeTimeZone}
                  // The reason an owner gave for being away, and who the
                  // desk went to, are theirs and their managers' to read.
                  // Booking on someone's behalf is not managing resources,
                  // so it must not open this; the desk's own owner is
                  // recognised inside the component from the releases it
                  // loads.
                  canSeeDetails={canManageResource(authenticatedUser, resource)}
                  showsSeatOwner={
                    dedicatedSeatCount(resource) > 1 && !hidesIdentity
                  }
                />
              ) : null}
              <RoomSelectionList
                rooms={selectedRooms}
                viewingRoomId={resource?.id}
                onView={viewRoom}
                onRemove={handleRemoveRoom}
              />
              <div className="v2-reservation">
                <Reserve showActions={false} isCompact={true} />
              </div>
            </div>
            {!isMd && <div className={"h-10"} />}
          </ScrollArea>
          <div
            className={cn(
              classes.ActionButtonsWrapper,
              !isMd && "fixed bottom-0 left-2"
            )}
          >
            <MapReserveCardActions
              onSubmit={onSubmit}
              canSelectMoreRooms={canSelectMoreRooms}
              roomsCount={selectedRooms.length}
              onStartPicking={handleStartPicking}
            />
          </div>
        </>
      )}
    </>
  );
};
